import { ApiError } from '../../../lib/apiClient';

/** Error codes the Fauna Marin endpoints answer with (backend: src/services/faunaMarinErrors.ts). */
export type FaunaMarinUiErrorCode =
  | 'PROVIDER_AUTH_FAILED'
  | 'PROVIDER_NOT_CONFIGURED'
  | 'PROVIDER_NO_AQUARIUM'
  | 'PROVIDER_RATE_LIMITED'
  | 'PROVIDER_PARSE_FAILED'
  | 'PROVIDER_NETWORK_ERROR'
  | 'PROVIDER_TOKEN_MISSING';

export interface FaunaMarinUiError {
  /** Translation key of the message (never raw backend text). */
  key: string;
  /** Interpolation values for the key. */
  values?: Record<string, number>;
  /** warning for "try again later" situations, danger otherwise. */
  tone: 'danger' | 'warning';
  code: string;
  legacyCredentialsRemoved: boolean;
}

const BASE = 'integrations.faunaMarin.errors';

function errorPayload(err: unknown): Record<string, unknown> {
  if (!(err instanceof ApiError)) return {};
  const data = err.data as { error?: Record<string, unknown> } | undefined;
  return data && typeof data.error === 'object' && data.error !== null ? data.error : {};
}

/** Maps an error code (+ optional details) to a translated message key. */
export function describeFaunaMarinError(code: string, details: Record<string, unknown> = {}): Omit<FaunaMarinUiError, 'code' | 'legacyCredentialsRemoved'> {
  switch (code) {
    case 'PROVIDER_AUTH_FAILED':
      return details['reason'] === 'partner_key'
        ? { key: `${BASE}.authPartner`, tone: 'danger' }
        : { key: `${BASE}.authToken`, tone: 'danger' };
    case 'PROVIDER_NOT_CONFIGURED':
      return { key: `${BASE}.notConfigured`, tone: 'warning' };
    case 'PROVIDER_NO_AQUARIUM':
      return { key: `${BASE}.noAquarium`, tone: 'danger' };
    case 'PROVIDER_RATE_LIMITED': {
      const seconds = typeof details['retryAfterSeconds'] === 'number' ? details['retryAfterSeconds'] : undefined;
      return seconds
        ? { key: `${BASE}.rateLimitedSeconds`, values: { seconds }, tone: 'warning' }
        : { key: `${BASE}.rateLimited`, tone: 'warning' };
    }
    case 'PROVIDER_NETWORK_ERROR':
      return { key: `${BASE}.network`, tone: 'warning' };
    case 'PROVIDER_PARSE_FAILED':
      return { key: `${BASE}.parse`, tone: 'danger' };
    case 'PROVIDER_TOKEN_MISSING':
      return { key: `${BASE}.tokenMissing`, tone: 'warning' };
    default:
      return { key: `${BASE}.generic`, tone: 'danger' };
  }
}

/** Turns a thrown value from a Fauna Marin request into a UI error. */
export function toFaunaMarinUiError(err: unknown): FaunaMarinUiError {
  const payload = errorPayload(err);
  const code = err instanceof ApiError ? err.code : 'UNKNOWN';
  return {
    ...describeFaunaMarinError(code, payload),
    code,
    legacyCredentialsRemoved: payload['legacyCredentialsRemoved'] === true,
  };
}
